import { registerSchema } from '@argus-labs/auth-shared/auth'
import { VerifyType } from '@argus-labs/auth-shared/verify'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  createFileRoute,
  Link,
  redirect,
  stripSearchParams,
  useNavigate,
} from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PageCard } from '@/components/ui/page-card'
import { Text } from '@/components/ui/text'
import { authClient } from '@/lib/auth-client'
import { authFlow } from '@/lib/auth-flow'
import { toast } from '@/lib/toast'
import { commonSearchSchema, generateTitle } from '@/lib/utils'

// Use with stripSearchParams middleware, so the browser URL remains clean
const defaultSearchParams = {
  currentStep: 'name',
} as const

const registerPageSchema = commonSearchSchema.extend({
  // Maintain current step in the URL, so that user can use the browser's back button to navigate back
  currentStep: z.enum(['name', 'email', 'password']).default(defaultSearchParams.currentStep),
  // As multistep progresses, keep previous steps' values in the URL
  name: z.string().optional(),
  email: z.email().optional(),
  // Email already exists, used in email form to show error message
  emailAlreadyExists: z.email().optional(),
})

export const Route = createFileRoute('/_signedOut/register')({
  validateSearch: registerPageSchema,
  component: Register,
  head: () => ({ meta: [{ title: generateTitle('Create Account') }] }),
  search: {
    middlewares: [stripSearchParams(defaultSearchParams)],
  },
  beforeLoad: ({ search }) => {
    const returnToFirstStep = () => {
      throw redirect({ to: '/register', search: { ...search, currentStep: 'name' } })
    }

    // Email is step 2, `name` must be carried over from step 1
    if (search.currentStep === 'email' && !search.name) {
      returnToFirstStep()
    }

    // Password is step 3, `name` and `email` must be carried over from step 1 and 2
    if (search.currentStep === 'password' && (!search.name || !search.email)) {
      returnToFirstStep()
    }
  },
})

const DESCRIPTIONS = {
  name: 'Tell us your full name so we can recognize you.',
  email: 'Tell us your email address for your identity in our world.',
  password: 'Create your password to seal your identity.',
}

function Register() {
  const search = Route.useSearch()
  const { currentStep, gameId } = search
  const isUpgrade = authFlow(search).kind === 'guestUpgrade'

  return (
    <PageCard
      title={isUpgrade ? 'Save Your Guest Progress' : 'Create Account'}
      description={
        isUpgrade ? (
          <>
            Create an Argus account to keep your guest progress in <b>{gameId}</b>.
          </>
        ) : (
          DESCRIPTIONS[currentStep]
        )
      }
    >
      <RegisterForm />

      <div className="mt-4 flex items-center gap-4">
        <Text size={12}>Already have an account?</Text>

        <Link to="/login" className="flex">
          <Text size={12} font="nbArchitekt" color="gold">
            Sign In
          </Text>
        </Link>
      </div>
    </PageCard>
  )
}

// Register is a multistep form
function RegisterForm() {
  const { currentStep, name, email, emailAlreadyExists } = Route.useSearch()

  switch (currentStep) {
    case 'name':
      return <NameForm />
    case 'email': {
      if (!name) {
        // Shouldn't happen because of beforeLoad in _signedOut/register.tsx
        throw new Error('Name is required when on email step')
      }

      return <EmailForm name={name} emailAlreadyExists={emailAlreadyExists} />
    }
    case 'password': {
      if (!name || !email) {
        // Shouldn't happen because of beforeLoad in _signedOut/register.tsx
        throw new Error('Name and email are required when on password step')
      }

      return <PasswordForm name={name} email={email} />
    }
    default:
      return null
  }
}

// Pick name from register params
const nameFormSchema = registerSchema.pick({ name: true })
type NameFormParams = z.infer<typeof nameFormSchema>

function NameForm() {
  const search = Route.useSearch()
  const form = useForm<NameFormParams>({
    resolver: zodResolver(nameFormSchema),
    defaultValues: { name: '' },
    mode: 'onChange',
  })
  const navigate = useNavigate()

  const onSubmit = async (values: NameFormParams) => {
    // Go to email form
    navigate({
      to: '/register',
      search: { ...search, currentStep: 'email', name: values.name },
    })
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-2">
      <Input
        control={form.control}
        type="text"
        name="name"
        label="Full Name"
        required
        autoFocus
        autoComplete="name"
        placeholder="eg: mojo doe"
      />

      <Button type="submit" flex>
        Next
      </Button>
    </form>
  )
}

// Pick email from register params and refine it
const emailFormSchema = registerSchema.pick({ email: true })
type EmailFormParams = z.infer<typeof emailFormSchema>

function EmailForm({
  name,
  emailAlreadyExists,
}: {
  name: string
  emailAlreadyExists: string | undefined
}) {
  // When email form renders with `emailAlreadyExists` search param, show error message if input email is equal to the email already exists
  const [finalEmailFormSchema] = useState(() => {
    if (emailAlreadyExists) {
      return emailFormSchema.extend({
        email: emailFormSchema.shape.email.refine((val) => val !== emailAlreadyExists, {
          message: 'An account with this email already exists',
        }),
      })
    }
    return emailFormSchema
  })

  const form = useForm<EmailFormParams>({
    resolver: zodResolver(finalEmailFormSchema),
    defaultValues: { email: emailAlreadyExists ?? '' },
    mode: 'onChange',
  })
  const navigate = useNavigate()

  // Use together with showErrorMessageOnPristineForm
  useEffect(() => {
    // Trigger form validation on mount
    form.trigger()
  }, [form.trigger])

  const onSubmit = async (values: EmailFormParams) => {
    // Go to password form
    navigate({
      to: '/register',
      search: { currentStep: 'password', name, email: values.email },
    })
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-2">
      <Input
        control={form.control}
        type="email"
        name="email"
        label="Email Address"
        required
        autoFocus
        autoComplete="email"
        placeholder="eg: mojo.doe@argus.gg"
        showErrorMessageOnPristineForm={!!emailAlreadyExists}
      />

      <Button type="submit" flex>
        Next
      </Button>
    </form>
  )
}

// Pick name from register params
const passwordFormSchema = registerSchema.pick({ password: true })
type PasswordFormParams = z.infer<typeof passwordFormSchema>

function PasswordForm({ name, email }: { name: string; email: string }) {
  const form = useForm<PasswordFormParams>({
    resolver: zodResolver(passwordFormSchema),
    defaultValues: { password: '' },
    mode: 'onChange',
  })
  const navigate = useNavigate()

  const onSubmit = async (values: PasswordFormParams) => {
    try {
      const { data, error } = await authClient.signUp.email({
        name,
        email,
        password: values.password,
      })

      if (data) {
        return navigate({
          to: '/verify',
          search: { email, type: VerifyType.ConfirmEmail },
        })
      }

      toast.fromError(error)
    } catch {
      toast.fromPreset('NETWORK_ERROR')
    }
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-2">
      <Input
        control={form.control}
        type="password"
        name="password"
        label="Password"
        required
        autoFocus
        autoComplete="new-password"
        placeholder=""
      />

      <Button type="submit" flex isDisabled={form.formState.isSubmitting}>
        Next
      </Button>
    </form>
  )
}
